import React, { RefObject, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import {
    DatePicker,
    Form,
    FormInstance,
    IconButton,
    InputGroup,
    InputPicker,
} from "rsuite";

import EditIcon from "@rsuite/icons/Edit";
import EyeRoundIcon from "@rsuite/icons/EyeRound";

import { isPreviousDate } from "utils/date-helper";

import { CustomerType } from "store/customers/add-new-customer/add-new-customer-types";
import {
    Area,
    IDooapAppRegistration,
} from "store/environments/environment-types";
import {
    ErpCredentials,
    AuthenticationMethod,
    AuthenticationType,
} from "store/environments/tenants/tenant-configurations/tenant-configurations-types";

import Copy from "components/Common/Copy/Copy";

interface IComponentProps {
    envMoniker: string;
    authMethods: AuthenticationMethod[];
    erpConnectionInfo: ErpCredentials | null;
    authenticationMethod: AuthenticationMethod | null;
    appRegistrations: IDooapAppRegistration[];
    customerType: CustomerType | null;
    formRef: RefObject<FormInstance> | null;
    setConnectionInfoChanged: (isChanged: boolean) => void;
    setErpConnectionInfo: (connectionInfo: ErpCredentials | null) => void;
    setAuthenticationMethod: (authMethod: AuthenticationMethod | null) => void;
    fetchDooapAppRegistrations: (envMoniker: string) => void;
    setIsUnsavedChangeAvailable: (isChangesAvailable: boolean) => void;
}

const initialEditableFields = {
    url: false,
    clientId: false,
    username: false,
    password: false,
    secret: false,
    expDate: false,
};

const ErpConnectionInfo: React.FC<IComponentProps> = ({
    envMoniker,
    authMethods,
    erpConnectionInfo,
    authenticationMethod,
    appRegistrations,
    customerType,
    formRef,
    setConnectionInfoChanged,
    setErpConnectionInfo,
    setAuthenticationMethod,
    fetchDooapAppRegistrations,
    setIsUnsavedChangeAvailable,
}: IComponentProps) => {
    const { t } = useTranslation();
    const [editableFields, setEditableFields] = useState(initialEditableFields);

    const [isPasswordShown, setIsPasswordShown] = useState<boolean>(false);
    const [isSecretShown, setIsSecretShown] = useState<boolean>(false);

    const [d365AppRegistration, setD365AppRegistration] =
        useState<IDooapAppRegistration | null>(null);

    useEffect(() => {
        if (!appRegistrations.length) {
            fetchDooapAppRegistrations(envMoniker);
        }
    }, [appRegistrations, envMoniker, fetchDooapAppRegistrations]);

    useEffect(() => {
        const appRegistration = appRegistrations.find(
            (registration) => registration.area === Area.D365
        );
        setD365AppRegistration(appRegistration as IDooapAppRegistration);
    }, [appRegistrations]);

    useEffect(() => {
        if (
            d365AppRegistration &&
            authenticationMethod &&
            authenticationMethod.authenticationType ===
                AuthenticationType.UsernamePassword &&
            !erpConnectionInfo?.clientId
        ) {
            setErpConnectionInfo({
                ...erpConnectionInfo,
                clientId: d365AppRegistration.clientId,
            } as ErpCredentials);
        }
    }, [d365AppRegistration, authenticationMethod]);

    useEffect(() => {
        const isUsernamePassword =
            authenticationMethod?.authenticationType ===
            AuthenticationType.UsernamePassword;

        setEditableFields({
            ...editableFields,
            username: !erpConnectionInfo?.username?.length,
            password: !erpConnectionInfo?.password?.length,
            secret: !erpConnectionInfo?.clientSecret?.length,
            expDate: isUsernamePassword
                ? editableFields.expDate
                : erpConnectionInfo?.secretExpirationDate === null,
        });
    }, [authenticationMethod]);

    useEffect(() => {
        formRef?.current?.check();
    }, [erpConnectionInfo]);

    const handlePasswordToggle = () =>
        setIsPasswordShown((isPasswordShown) => !isPasswordShown);

    const handleSecretToggle = () =>
        setIsSecretShown((isSecretShown) => !isSecretShown);

    const handlePasswordMouseDown = () => {
        setIsPasswordShown(true);
    };

    const handleSecretMouseDown = () => {
        setIsSecretShown(true);
    };

    const handleChange = (name: keyof ErpCredentials, value: string | Date) => {
        setErpConnectionInfo({
            ...erpConnectionInfo,
            [name]: value,
        } as ErpCredentials);
        setConnectionInfoChanged(true);
        setIsUnsavedChangeAvailable(true);
    };

    const handlePasswordChange = (value: string) => {
        setErpConnectionInfo({
            ...erpConnectionInfo,
            password: value,
        } as ErpCredentials);

        setConnectionInfoChanged(true);
        setIsUnsavedChangeAvailable(true);
    };

    const handleSecretChange = (value: string) => {
        setErpConnectionInfo({
            ...erpConnectionInfo,
            clientSecret: value,
            secretExpirationDate: null,
        } as ErpCredentials);

        setEditableFields({
            ...editableFields,
            expDate: true,
        });

        setConnectionInfoChanged(true);
        setIsUnsavedChangeAvailable(true);
    };

    const handleAuthMethodChange = (value: AuthenticationType) => {
        if (value === authenticationMethod?.authenticationType) {
            return;
        }

        setAuthenticationMethod(
            authMethods.find((t) => t.authenticationType === value) ?? null
        );

        switch (value) {
            case AuthenticationType.UsernamePassword:
                setErpConnectionInfo({
                    ...erpConnectionInfo,
                    clientSecret: null,
                    secretExpirationDate: null,
                    clientId: erpConnectionInfo?.clientId || "",
                } as ErpCredentials);
                break;
            default:
                setErpConnectionInfo({
                    ...erpConnectionInfo,
                    password: null,
                    clientId: d365AppRegistration?.clientId || "",
                } as ErpCredentials);
                break;
        }

        setConnectionInfoChanged(true);
        setIsUnsavedChangeAvailable(true);
    };

    const formKey: Record<keyof ErpCredentials, keyof ErpCredentials> = {
        url: "url",
        username: "username",
        clientId: "clientId",
        secretExpirationDate: "secretExpirationDate",
        clientSecret: "clientSecret",
        password: "password",
    };

    const authMethodData = authMethods
        .filter((authMethod) =>
            customerType === CustomerType.AX2012
                ? authMethod.authenticationType ===
                  AuthenticationType.UsernamePassword
                : true
        )
        .map((m: AuthenticationMethod) => ({
            label: m.authenticationDescription,
            value: m.authenticationType,
        }));

    const getAuthFragment = () => {
        switch (authenticationMethod?.authenticationType) {
            case AuthenticationType.ClientSecret:
                return (
                    <>
                        <Form.Group
                            className={"general-configurations__grid-item"}
                        >
                            <Form.ControlLabel
                                className={"general-configurations__label"}
                            >
                                {t(
                                    "tenantConfig.d365InformationPage.clientSecretTitle"
                                )}
                            </Form.ControlLabel>
                            <div
                                className={"general-configurations__auth-field"}
                            >
                                <InputGroup
                                    inside
                                    className={
                                        "general-configurations__input-group"
                                    }
                                >
                                    <Form.Control
                                        readOnly={!editableFields.secret}
                                        className={cn(
                                            "general-configurations__form-control",
                                            !editableFields.secret
                                                ? "general-configurations__disabled"
                                                : ""
                                        )}
                                        type={
                                            isSecretShown ? "text" : "password"
                                        }
                                        name={formKey.clientSecret}
                                        onChange={handleSecretChange}
                                    />
                                    <InputGroup.Addon
                                        onClick={handleSecretToggle}
                                        onMouseDown={handleSecretMouseDown}
                                        className={
                                            "general-configurations__image-section"
                                        }
                                    >
                                        <EyeRoundIcon />
                                    </InputGroup.Addon>
                                </InputGroup>
                                <IconButton
                                    disabled={editableFields.secret}
                                    appearance="subtle"
                                    size="md"
                                    onClick={() => {
                                        setEditableFields({
                                            ...editableFields,
                                            secret: true,
                                        });
                                    }}
                                    icon={<EditIcon />}
                                    className={
                                        "general-configurations__pencil-icon"
                                    }
                                />
                                {!editableFields.secret && (
                                    <Copy
                                        value={
                                            erpConnectionInfo?.clientSecret ??
                                            ""
                                        }
                                    />
                                )}
                            </div>
                        </Form.Group>
                        <Form.Group
                            className={"general-configurations__grid-item"}
                        >
                            <Form.ControlLabel
                                className={"general-configurations__label"}
                            >
                                {t(
                                    "tenantConfig.d365InformationPage.clientSecretExpDateTitle"
                                )}
                            </Form.ControlLabel>
                            <div
                                className={"general-configurations__auth-field"}
                            >
                                <Form.Control
                                    editable={false}
                                    accepter={DatePicker}
                                    readOnly={!editableFields.expDate}
                                    name={formKey.secretExpirationDate}
                                    value={
                                        erpConnectionInfo?.secretExpirationDate
                                            ? new Date(
                                                  erpConnectionInfo?.secretExpirationDate
                                              )
                                            : null
                                    }
                                    className={cn(
                                        "general-configurations__form-control",
                                        !editableFields.expDate
                                            ? "general-configurations__disabled"
                                            : ""
                                    )}
                                    cleanable={false}
                                    ranges={[]}
                                    placement={"topStart"}
                                    onChange={(value: any) => {
                                        handleChange(
                                            formKey.secretExpirationDate,
                                            (value as Date).toISOString()
                                        );
                                    }}
                                    shouldDisableDate={(
                                        date: Date | undefined
                                    ) => (date ? isPreviousDate(date) : false)}
                                />
                            </div>
                        </Form.Group>
                    </>
                );
            case AuthenticationType.UsernamePassword:
                return (
                    <Form.Group className={"general-configurations__grid-item"}>
                        <Form.ControlLabel
                            className={"general-configurations__label"}
                        >
                            {t(
                                "tenantConfig.d365InformationPage.passwordTitle"
                            )}
                        </Form.ControlLabel>
                        <div className={"general-configurations__auth-field"}>
                            <InputGroup
                                inside
                                className={
                                    "general-configurations__input-group"
                                }
                            >
                                <Form.Control
                                    readOnly={!editableFields.password}
                                    className={cn(
                                        "general-configurations__form-control",
                                        !editableFields.password
                                            ? "general-configurations__disabled"
                                            : ""
                                    )}
                                    type={isPasswordShown ? "text" : "password"}
                                    name={formKey.password}
                                    onChange={handlePasswordChange}
                                />
                                <InputGroup.Addon
                                    onClick={handlePasswordToggle}
                                    onMouseDown={handlePasswordMouseDown}
                                    className={
                                        "general-configurations__image-section"
                                    }
                                >
                                    <EyeRoundIcon />
                                </InputGroup.Addon>
                            </InputGroup>
                            <IconButton
                                disabled={editableFields.password}
                                appearance="subtle"
                                size="md"
                                onClick={() => {
                                    setEditableFields({
                                        ...editableFields,
                                        password: true,
                                    });
                                }}
                                icon={<EditIcon />}
                                className={
                                    "general-configurations__pencil-icon"
                                }
                            />
                            {!editableFields.password && (
                                <Copy
                                    value={erpConnectionInfo?.password ?? ""}
                                />
                            )}
                        </div>
                    </Form.Group>
                );

            default:
                return <></>;
        }
    };

    const authFragment = getAuthFragment();

    return (
        <>
            <Form.Group className={"general-configurations__grid-item"}>
                <Form.ControlLabel className={"general-configurations__label"}>
                    {t("tenantConfig.d365InformationPage.erpUrlTitle")}
                </Form.ControlLabel>
                <div className={"general-configurations__auth-field"}>
                    <Form.Control
                        readOnly={!editableFields.url}
                        className={cn(
                            "general-configurations__form-control",
                            !editableFields.url
                                ? "general-configurations__disabled"
                                : ""
                        )}
                        name={formKey.url}
                        onChange={(value: string) => {
                            handleChange(formKey.url, value);
                        }}
                    />
                    <IconButton
                        disabled={editableFields.url}
                        appearance="subtle"
                        size="md"
                        onClick={() => {
                            setEditableFields({
                                ...editableFields,
                                url: true,
                            });
                        }}
                        icon={<EditIcon />}
                        className={"general-configurations__pencil-icon"}
                    />
                    {!editableFields.url && (
                        <Copy value={erpConnectionInfo?.url ?? ""} />
                    )}
                </div>
            </Form.Group>
            <Form.Group className={"general-configurations__grid-item"}>
                <Form.ControlLabel className={"general-configurations__label"}>
                    {t("tenantConfig.d365InformationPage.authMethodTitle")}
                </Form.ControlLabel>
                <Form.Control
                    accepter={InputPicker}
                    data={authMethodData}
                    name="authenticationMethod"
                    cleanable={false}
                    searchable={false}
                    onChange={handleAuthMethodChange}
                    className={cn(
                        "general-configurations__form-control",
                        "general-configurations__company-input-picker",
                        "vertically-aligned-picker"
                    )}
                />
            </Form.Group>
            <Form.Group className={"general-configurations__grid-item"}>
                <Form.ControlLabel className={"general-configurations__label"}>
                    {t("tenantConfig.d365InformationPage.clientIdTitle")}
                </Form.ControlLabel>
                <div className={"general-configurations__auth-field"}>
                    <Form.Control
                        readOnly={!editableFields.clientId}
                        className={cn(
                            "general-configurations__form-control",
                            !editableFields.clientId
                                ? "general-configurations__disabled"
                                : ""
                        )}
                        name={formKey.clientId}
                        onChange={(value: string) => {
                            handleChange(formKey.clientId, value);
                        }}
                    />
                    <IconButton
                        disabled={editableFields.clientId}
                        appearance="subtle"
                        size="md"
                        onClick={() => {
                            setEditableFields({
                                ...editableFields,
                                clientId: true,
                            });
                        }}
                        icon={<EditIcon />}
                        className={"general-configurations__pencil-icon"}
                    />
                    {!editableFields.clientId && (
                        <Copy value={erpConnectionInfo?.clientId ?? ""} />
                    )}
                </div>
            </Form.Group>
            <Form.Group className={"general-configurations__grid-item"}>
                <Form.ControlLabel className={"general-configurations__label"}>
                    {t("tenantConfig.d365InformationPage.usernameTitle")}
                </Form.ControlLabel>
                <div className={"general-configurations__auth-field"}>
                    <Form.Control
                        readOnly={!editableFields.username}
                        className={cn(
                            "general-configurations__form-control",
                            !editableFields.username
                                ? "general-configurations__disabled"
                                : ""
                        )}
                        name={formKey.username}
                        onChange={(value: string) => {
                            handleChange(formKey.username, value);
                        }}
                    />
                    <IconButton
                        disabled={editableFields.username}
                        appearance="subtle"
                        size="md"
                        onClick={() => {
                            setEditableFields({
                                ...editableFields,
                                username: true,
                            });
                        }}
                        icon={<EditIcon />}
                        className={"general-configurations__pencil-icon"}
                    />
                    {!editableFields.username && (
                        <Copy value={erpConnectionInfo?.username ?? ""} />
                    )}
                </div>
            </Form.Group>
            {authFragment}
        </>
    );
};

export default ErpConnectionInfo;
